import { useEffect, useMemo, useState, useRef } from 'react';
import { useStore } from '../store/useStore.js';
import type { GraphNode } from '@synapse/engine';
import { CATEGORY_MAP, type CategoryKey } from '../utils/constants.js';
import { Upload, Loader2 } from 'lucide-react';
import { MicButton } from './MicButton.js';

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function Icon({ name, size = 18 }: { name: string; size?: number }) {
  return <span className="material-symbols-outlined" style={{ fontSize: size }}>{name}</span>;
}

export function ListView() {
  const nodes = useStore((s) => s.nodes);
  const links = useStore((s) => s.links);
  const setSelected = useStore((s) => s.setSelected);
  const selectedId = useStore((s) => s.selectedId);
  const updateNode = useStore((s) => s.updateNode);
  const addNode = useStore((s) => s.addNode);
  const docTabs = useStore((s) => s.docTabs);
  const openDocTab = useStore((s) => s.openDocTab);
  const closeDocTab = useStore((s) => s.closeDocTab);

  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [treeQ, setTreeQ] = useState('');
  const [dragOverId, setDragOverId] = useState<string | 'root' | null>(null);
  const selectedNode = nodes.find((n) => n.id === selectedId);

  // true se `maybeDescendantId` está na subárvore de `ancestorId` (evita ciclo no drop).
  function isDescendantOf(ancestorId: string, maybeDescendantId: string): boolean {
    let curr = nodes.find((n) => n.id === maybeDescendantId);
    const seen = new Set<string>();
    while (curr?.parentId) {
      if (curr.parentId === ancestorId) return true;
      if (seen.has(curr.parentId)) break;
      seen.add(curr.parentId);
      curr = nodes.find((n) => n.id === curr!.parentId);
    }
    return false;
  }

  function handleTreeDrop(targetId: string | null, e: React.DragEvent) {
    e.preventDefault();
    e.stopPropagation();
    setDragOverId(null);
    const draggedId = e.dataTransfer.getData('text/plain');
    if (!draggedId) return;
    const dragged = nodes.find((n) => n.id === draggedId);
    if (!dragged) return;
    if (draggedId === targetId) return;
    if ((dragged.parentId ?? null) === (targetId ?? null)) return;
    // Não pode aninhar um nó dentro da própria subárvore.
    if (targetId && isDescendantOf(draggedId, targetId)) return;
    updateNode(draggedId, { parentId: targetId });
    if (targetId) {
      setCollapsed((prev) => { const next = new Set(prev); next.delete(targetId); return next; });
    }
  }

  const workspaceId = useStore((s) => s.workspaceId);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  async function handleFileUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file || !workspaceId || !selectedNode) return;
    setIsUploading(true);
    setUploadError(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('workspaceId', workspaceId);
      const res = await fetch(`/api/assets/upload-direct?workspaceId=${workspaceId}`, {
        method: 'POST',
        body: formData,
      });
      if (!res.ok) throw new Error('Falha no upload');
      const data = await res.json();
      
      updateNode(selectedNode.id, { content: data.assetId });
    } catch (err) {
      console.error(err);
      setUploadError('Erro ao enviar a imagem. Tente novamente.');
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }


  const [slashMenu, setSlashMenu] = useState<{
    visible: boolean;
    x: number;
    y: number;
    query: string;
    index: number;
  }>({
    visible: false,
    x: 0,
    y: 0,
    query: '',
    index: 0
  });

  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Autosave do conteúdo enquanto digita (sem depender do blur).
  const pendingSaveRef = useRef<{ id: string; value: string } | null>(null);
  const saveTimerRef = useRef<number | null>(null);

  function flushContentSave() {
    if (saveTimerRef.current !== null) {
      window.clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
    const p = pendingSaveRef.current;
    pendingSaveRef.current = null;
    if (!p) return;
    const cur = useStore.getState().nodes.find((n) => n.id === p.id);
    if (cur && (cur.content ?? '') !== p.value) updateNode(p.id, { content: p.value });
  }

  function queueContentSave(id: string, value: string) {
    pendingSaveRef.current = { id, value };
    if (saveTimerRef.current !== null) window.clearTimeout(saveTimerRef.current);
    saveTimerRef.current = window.setTimeout(flushContentSave, 800);
  }

  useEffect(() => flushContentSave, []);

  function insertTextAtCursor(textToInsert: string, removeSlash = false) {
    const textarea = textareaRef.current;
    if (!textarea || !selectedNode) return;
    
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const value = textarea.value;
    
    let newValue = value;
    let newCursorPos = start + textToInsert.length;
    
    if (removeSlash && start > 0) {
      const slashIndex = value.lastIndexOf('/', start - 1);
      if (slashIndex !== -1) {
        newValue = value.slice(0, slashIndex) + textToInsert + value.slice(end);
        newCursorPos = slashIndex + textToInsert.length;
      }
    } else {
      newValue = value.slice(0, start) + textToInsert + value.slice(end);
    }
    
    textarea.value = newValue;
    textarea.focus();
    textarea.setSelectionRange(newCursorPos, newCursorPos);
    
    updateNode(selectedNode.id, { content: newValue });
    setSlashMenu(prev => ({ ...prev, visible: false }));
  }

  const slashItems = [
    { label: 'Data Atual', desc: 'Insere a data de hoje', icon: 'calendar_month', color: '#ffcc00', action: () => insertTextAtCursor(' [' + new Date().toLocaleDateString('pt-BR') + '] ', true) },
    { label: 'Checkbox', desc: 'Cria uma lista de afazeres', icon: 'check_box', color: '#00ff88', action: () => insertTextAtCursor('\n[ ] ', true) },
    { label: 'Marcador / Destaque', desc: 'Destaca o texto', icon: 'border_color', color: '#00f5ff', action: () => insertTextAtCursor('==texto destacado==', true) },
    { label: 'Título Principal', desc: 'Título grande H1', icon: 'format_h1', color: '#bf00ff', action: () => insertTextAtCursor('\n# ', true) },
    { label: 'Subtítulo', desc: 'Título médio H2', icon: 'format_h2', color: '#ff007f', action: () => insertTextAtCursor('\n## ', true) },
  ];

  const getFilteredSlashItems = () => {
    if (!slashMenu.query) return slashItems;
    return slashItems.filter(item => 
      item.label.toLowerCase().includes(slashMenu.query.toLowerCase()) ||
      item.desc.toLowerCase().includes(slashMenu.query.toLowerCase())
    );
  };

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const textarea = e.target;
    const { selectionStart, value } = textarea;

    if (selectedNode) queueContentSave(selectedNode.id, value);

    const textBeforeCaret = value.slice(0, selectionStart);
    const lastChar = textBeforeCaret.slice(-1);
    
    if (lastChar === '/') {
      const lines = textBeforeCaret.split('\n');
      const currentLine = lines.length;
      const currentChar = lines[lines.length - 1]?.length ?? 0;
      
      setSlashMenu({
        visible: true,
        x: Math.min(textarea.clientWidth - 240, Math.max(20, currentChar * 8 + 20)),
        y: Math.min(textarea.clientHeight - 180, currentLine * 22 + 20),
        query: '',
        index: 0
      });
    } else if (slashMenu.visible) {
      const match = textBeforeCaret.match(/\/(\w*)$/);
      if (match) {
        setSlashMenu(prev => ({ ...prev, query: match[1] || '' }));
      } else {
        setSlashMenu(prev => ({ ...prev, visible: false }));
      }
    }
  };

  const handleTextareaKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (!slashMenu.visible) return;
    
    const menuItems = getFilteredSlashItems();
    if (menuItems.length === 0) return;
    
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSlashMenu(prev => ({ ...prev, index: (prev.index + 1) % menuItems.length }));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSlashMenu(prev => ({ ...prev, index: (prev.index - 1 + menuItems.length) % menuItems.length }));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const selectedItem = menuItems[slashMenu.index];
      if (selectedItem) {
        selectedItem.action();
      }
      setSlashMenu(prev => ({ ...prev, visible: false }));
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setSlashMenu(prev => ({ ...prev, visible: false }));
    }
  };

  function toggleCollapse(id: string) {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  function buildTree(parentId: string | null): GraphNode[] {
    const items = nodes.filter((n) => (n.parentId ?? null) === parentId);
    if (!treeQ) return items;
    return items.filter((n) =>
      n.label.toLowerCase().includes(treeQ.toLowerCase())
    );
  }

  function renderTree(parentId: string | null, depth: number): React.ReactNode {
    const items = buildTree(parentId);
    if (!items.length) return null;
    return items.map((n) => {
      const isSelected = selectedId === n.id;
      const hasChildren = nodes.some((c) => c.parentId === n.id);
      const isCollapsed = collapsed.has(n.id);

      return (
        <div key={n.id}>
          <div
            className={`syn-tree-item depth-${Math.min(depth, 2)}${isSelected ? ' is-active' : ''}`}
            style={{
              '--c': n.color,
              ...(dragOverId === n.id ? { outline: '1.5px dashed var(--neon)', outlineOffset: -2, borderRadius: 6 } : {}),
            } as React.CSSProperties}
            onClick={() => setSelected(n.id)}
            onDoubleClick={() => { openDocTab(n.id); setSelected(n.id); }}
            draggable
            onDragStart={(e) => {
              e.dataTransfer.setData('text/plain', n.id);
              e.dataTransfer.effectAllowed = 'move';
            }}
            onDragOver={(e) => {
              e.preventDefault();
              e.stopPropagation();
              e.dataTransfer.dropEffect = 'move';
              if (dragOverId !== n.id) setDragOverId(n.id);
            }}
            onDragLeave={() => { if (dragOverId === n.id) setDragOverId(null); }}
            onDrop={(e) => handleTreeDrop(n.id, e)}
          >
            {/* Collapse arrow */}
            {hasChildren ? (
              <button
                onClick={(e) => { e.stopPropagation(); toggleCollapse(n.id); }}
                style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', display: 'flex', color: 'var(--muted)', flexShrink: 0 }}
              >
                <span
                  className={`syn-tree-chev${isCollapsed ? ' is-collapsed' : ''}`}
                >
                  <Icon name="keyboard_arrow_down" size={14} />
                </span>
              </button>
            ) : (
              <span className="dot" />
            )}

            <span className="ico">
              <Icon name={n.icon ?? 'lightbulb'} size={14} />
            </span>
            <span className="name">{n.label}</span>

            {/* Category badge */}
            <span className="badge">
              {(CATEGORY_MAP[(n.category as CategoryKey) ?? 'idea']?.label ?? n.category).slice(0, 3).toUpperCase()}
            </span>

            {/* Add child */}
            <button
              className="row-act"
              title="Adicionar subitem"
              onClick={(e) => {
                e.stopPropagation();
                const newId = addNode({
                  label: 'Nova Ideia', type: 'idea', category: 'idea', icon: 'lightbulb',
                  parentId: n.id, x: Math.random() * 200 - 100, y: Math.random() * 200 - 100, size: 22,
                });
                openDocTab(newId);
                setSelected(newId);
                setCollapsed((prev) => { const next = new Set(prev); next.delete(n.id); return next; });
              }}
            >
              <Icon name="add" size={13} />
            </button>
          </div>
          {hasChildren && !isCollapsed && renderTree(n.id, depth + 1)}
        </div>
      );
    });
  }

  // Extract headings from content for outline
  const headings = useMemo(() => {
    if (!selectedNode?.content) return [];
    const lines = selectedNode.content.split('\n');
    return lines
      .map((line) => {
        const m = line.match(/^(#{1,2})\s+(.+)/);
        if (!m || !m[1] || !m[2]) return null;
        return { level: m[1].length, text: m[2] };
      })
      .filter(Boolean) as Array<{ level: number; text: string }>;
  }, [selectedNode?.content]);

  // Abas abrem somente com duplo clique (seleção simples não polui a tab bar).
  // Aqui só limpamos abas de nós deletados.
  useEffect(() => {
    const liveIds = new Set(nodes.map((n) => n.id));
    for (const t of docTabs) {
      if (!liveIds.has(t)) closeDocTab(t);
    }
  }, [nodes, docTabs, closeDocTab]);

  // Backlinks — nodes connected to selected
  const backlinks = useMemo(() => {
    if (!selectedId) return [];
    return links
      .filter((l) => l.source === selectedId || l.target === selectedId)
      .map((l) => {
        const otherId = l.source === selectedId ? l.target : l.source;
        return nodes.find((n) => n.id === otherId);
      })
      .filter(Boolean) as GraphNode[];
  }, [selectedId, links, nodes]);

  return (
    <div className="syn-docs">
      {/* Tree sidebar — soltar no fundo move a página pra raiz */}
      <aside
        className="syn-tree"
        onDragOver={(e) => { e.preventDefault(); if (dragOverId !== 'root') setDragOverId('root'); }}
        onDragLeave={() => { if (dragOverId === 'root') setDragOverId(null); }}
        onDrop={(e) => handleTreeDrop(null, e)}
        style={dragOverId === 'root' ? { boxShadow: 'inset 0 0 0 1.5px rgba(0,245,255,0.35)' } : undefined}
      >
        <div className="syn-tree-hd">
          <span className="h">Documentos</span>
          <button
            className="add"
            title="Novo documento"
            onClick={() => {
              const id = addNode({ label: 'Nova Ideia', type: 'idea', category: 'idea', icon: 'lightbulb', x: 0, y: 0, size: 22 });
              openDocTab(id);
              setSelected(id);
            }}
          >
            <Icon name="add" size={16} />
          </button>
        </div>

        <div className="syn-tree-search">
          <Icon name="search" size={14} />
          <input
            value={treeQ}
            onChange={(e) => setTreeQ(e.target.value)}
            placeholder="Filtrar…"
          />
        </div>

        {nodes.length === 0 ? (
          <div style={{ padding: '24px 16px', fontSize: 12, color: 'var(--muted)', textAlign: 'center', lineHeight: 1.6 }}>
            Sem documentos.<br />Volte ao Flow e crie o primeiro nó.
          </div>
        ) : (
          renderTree(null, 0)
        )}
      </aside>

      {/* Editor */}
      {selectedNode ? (
        <div className="syn-editor">
          {docTabs.length > 0 && (
            <div className="syn-doctabs" role="tablist">
              {docTabs.map((tabId) => {
                const tabNode = nodes.find((n) => n.id === tabId);
                if (!tabNode) return null;
                const isActive = tabId === selectedId;
                return (
                  <div
                    key={tabId}
                    role="tab"
                    aria-selected={isActive}
                    className={`syn-doctab${isActive ? ' is-active' : ''}`}
                    style={{ '--c': tabNode.color } as React.CSSProperties}
                    onClick={() => setSelected(tabId)}
                    onMouseDown={(e) => { if (e.button === 1) { e.preventDefault(); closeDocTab(tabId); } }}
                  >
                    <span className="dot" />
                    <span className="lbl" title={tabNode.label}>{tabNode.label || 'Sem título'}</span>
                    <button
                      className="cls"
                      aria-label={`Fechar ${tabNode.label}`}
                      onClick={(e) => { e.stopPropagation(); closeDocTab(tabId); }}
                    >×</button>
                  </div>
                );
              })}
            </div>
          )}
          <div className="syn-editor-inner">
            <div className="syn-editor-meta-top">
              <span
                className="syn-cat-chip"
                style={{ '--c': selectedNode.color } as React.CSSProperties}
              >
                <span className="dot" />
                {CATEGORY_MAP[(selectedNode.category as CategoryKey) ?? 'idea']?.label ?? selectedNode.category}
              </span>
              <span style={{ color: 'var(--muted-3)' }}>·</span>
              <span>
                <Icon name={selectedNode.icon ?? 'lightbulb'} size={12} />
              </span>
              <button
                title="Localizar no Flow"
                onClick={() => {
                  const st = useStore.getState();
                  st.setViewMode('graph');
                  st.setFocusedParentId(selectedNode.parentId ?? null);
                  st.setCamera({ x: -selectedNode.x, y: -selectedNode.y, zoom: Math.max(st.cam.zoom, 0.8) });
                  st.setSelected(selectedNode.id);
                }}
                style={{
                  marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 5,
                  background: 'rgba(0,245,255,0.06)', border: '1px solid rgba(0,245,255,0.18)',
                  borderRadius: 6, padding: '3px 9px', fontSize: 11, fontWeight: 600,
                  color: 'var(--neon)', cursor: 'pointer',
                }}
              >
                <Icon name="my_location" size={13} />
                Localizar no Flow
              </button>
            </div>

            <input
              className="syn-edit-title-big"
              key={selectedNode.id + '-title'}
              defaultValue={selectedNode.label}
              onBlur={(e) => {
                const v = e.target.value.trim();
                if (v && v !== selectedNode.label) updateNode(selectedNode.id, { label: v });
              }}
              onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
              placeholder="Sem título"
            />

            <div className="syn-editor-sub">
              {(() => {
                const path: GraphNode[] = [];
                let curr = nodes.find((n) => n.id === selectedNode.parentId);
                while (curr) { path.unshift(curr); curr = nodes.find((n) => n.id === curr!.parentId); }
                return path.length > 0 ? (
                  <>
                    {path.map((p, i) => (
                      <span key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        {i > 0 && <span className="dot-sep">/</span>}
                        <button className="crumb" onClick={() => setSelected(p.id)}>{p.label}</button>
                      </span>
                    ))}
                    <span className="dot-sep">/</span>
                    <span>{selectedNode.label}</span>
                  </>
                ) : selectedNode.type === 'idea' ? null : (
                  <span style={{ color: 'var(--muted-2)' }}>Documento raiz</span>
                );
              })()}
            </div>
            <div className="syn-editor-divider" />

            {selectedNode.type === 'image' ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16, padding: '24px 0' }}>
                {isUploading ? (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 48, gap: 10, background: 'rgba(255,255,255,0.01)', borderRadius: 8, border: '1px dashed rgba(255,204,0,0.2)' }}>
                    <Loader2 className="animate-spin" size={32} style={{ color: '#ffcc00' }} />
                    <span style={{ fontSize: 14, color: 'var(--muted)' }}>Enviando imagem...</span>
                  </div>
                ) : selectedNode.content && typeof selectedNode.content === 'string' && selectedNode.content.trim() ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    <div style={{ width: '100%', borderRadius: 8, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.08)', background: 'rgba(0,0,0,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 12 }}>
                      <img
                        src={UUID_REGEX.test(selectedNode.content.trim()) ? `/api/assets/${selectedNode.content.trim()}/serve` : selectedNode.content.trim()}
                        alt={selectedNode.label}
                        style={{ maxWidth: '100%', maxHeight: 380, objectFit: 'contain', borderRadius: 6 }}
                      />
                    </div>
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileUpload}
                      accept="image/*"
                      style={{ display: 'none' }}
                    />
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      style={{
                        padding: '10px 16px',
                        background: 'rgba(255, 255, 255, 0.05)',
                        border: '1px solid rgba(255, 204, 0, 0.25)',
                        borderRadius: 6,
                        color: 'white',
                        cursor: 'pointer',
                        fontSize: 13,
                        fontWeight: 500,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 8,
                        width: '100%',
                        transition: 'all 0.2s',
                      }}
                    >
                      <Upload size={14} /> Substituir Imagem
                    </button>
                  </div>
                ) : (
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    style={{
                      border: '1px dashed rgba(255, 204, 0, 0.35)',
                      borderRadius: 8,
                      padding: '48px 24px',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 12,
                      cursor: 'pointer',
                      background: 'rgba(255,255,255,0.01)',
                      transition: 'all 0.2s',
                    }}
                  >
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileUpload}
                      accept="image/*"
                      style={{ display: 'none' }}
                    />
                    <Upload size={24} style={{ color: '#ffcc00' }} />
                    <span style={{ fontSize: 14, fontWeight: 500, color: 'white' }}>Clique para enviar imagem</span>
                    <span style={{ fontSize: 11, color: 'var(--muted-2)' }}>Formatos suportados: PNG, JPG, WEBP</span>
                  </div>
                )}
                {uploadError && (
                  <div style={{ fontSize: 12, color: 'var(--error, #ef4444)', marginTop: 8, textAlign: 'center' }}>
                    {uploadError}
                  </div>
                )}
              </div>
            ) : (
              <div style={{ position: 'relative', flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                <textarea
                  ref={textareaRef}
                  key={selectedNode.id + '-content'}
                  className="syn-editor-body"
                  defaultValue={selectedNode.content ?? ''}
                  onChange={handleTextareaChange}
                  onKeyDown={handleTextareaKeyDown}
                  onBlur={(e) => {
                    pendingSaveRef.current = { id: selectedNode.id, value: e.target.value };
                    flushContentSave();
                  }}
                  placeholder="Comece a escrever… Use # para títulos, ## para subtítulos. Digite '/' para abrir o menu de ações rápidos."
                  style={{ flex: 1, paddingRight: 56, minHeight: 300 }}
                />

                {/* Barra de ações rápida na direita */}
                <div
                  style={{
                    position: 'absolute',
                    right: 12,
                    top: 12,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 8,
                    background: 'rgba(10, 10, 15, 0.85)',
                    backdropFilter: 'blur(8px)',
                    border: '1px solid rgba(255, 255, 255, 0.08)',
                    borderRadius: 8,
                    padding: '6px',
                    zIndex: 10,
                    boxShadow: '0 8px 32px rgba(0,0,0,0.5), 0 0 10px rgba(0, 245, 255, 0.1)',
                  }}
                >
                  {[
                    { icon: 'calendar_month', title: 'Adicionar Data', color: '#ffcc00', glow: 'rgba(255,204,0,0.3)', action: () => insertTextAtCursor(' [' + new Date().toLocaleDateString('pt-BR') + '] ') },
                    { icon: 'check_box', title: 'Adicionar Checkbox', color: '#00ff88', glow: 'rgba(0,255,136,0.3)', action: () => insertTextAtCursor('\n[ ] ') },
                    { icon: 'border_color', title: 'Destacar Texto', color: '#00f5ff', glow: 'rgba(0,245,255,0.3)', action: () => insertTextAtCursor('==texto destacado==') },
                    { icon: 'format_h1', title: 'Título Principal', color: '#bf00ff', glow: 'rgba(191,0,255,0.3)', action: () => insertTextAtCursor('\n# ') },
                    { icon: 'format_h2', title: 'Subtítulo', color: '#ff007f', glow: 'rgba(255,0,127,0.3)', action: () => insertTextAtCursor('\n## ') },
                  ].map((item, idx) => (
                    <button
                      key={idx}
                      onClick={item.action}
                      title={item.title}
                      style={{
                        width: 32,
                        height: 32,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: 'transparent',
                        border: 'none',
                        borderRadius: 6,
                        color: item.color,
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                        e.currentTarget.style.boxShadow = `0 0 8px ${item.glow}`;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'transparent';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    >
                      <Icon name={item.icon} size={16} />
                    </button>
                  ))}
                  <MicButton size={16} onText={(t) => insertTextAtCursor(' ' + t + ' ')} />
                </div>

                {/* Popover flutuante do comando / */}
                {slashMenu.visible && getFilteredSlashItems().length > 0 && (
                  <div
                    style={{
                      position: 'absolute',
                      left: slashMenu.x,
                      top: slashMenu.y,
                      width: 240,
                      maxHeight: 280,
                      overflowY: 'auto',
                      background: 'rgba(10, 10, 15, 0.95)',
                      backdropFilter: 'blur(12px)',
                      border: '1px solid rgba(255, 255, 255, 0.08)',
                      borderRadius: 8,
                      padding: 4,
                      zIndex: 100,
                      boxShadow: '0 12px 48px rgba(0,0,0,0.8), 0 0 15px rgba(0, 245, 255, 0.15)',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 2,
                    }}
                  >
                    <div style={{ fontSize: 9, textTransform: 'uppercase', color: 'var(--muted-2)', padding: '6px 8px', letterSpacing: 1, fontFamily: 'var(--font-mono)' }}>
                      Comandos Rápidos
                    </div>
                    {getFilteredSlashItems().map((item, idx) => {
                      const isSelected = idx === slashMenu.index;
                      return (
                        <button
                          key={idx}
                          onClick={item.action}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 10,
                            width: '100%',
                            padding: '8px 10px',
                            background: isSelected ? 'rgba(255, 255, 255, 0.06)' : 'transparent',
                            border: 'none',
                            borderRadius: 6,
                            textAlign: 'left',
                            cursor: 'pointer',
                            transition: 'all 0.15s',
                          }}
                        >
                          <div style={{
                            width: 28,
                            height: 28,
                            borderRadius: 6,
                            background: isSelected ? item.color : 'rgba(255,255,255,0.02)',
                            color: isSelected ? '#0a0a0f' : item.color,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.15s',
                          }}>
                            <Icon name={item.icon} size={15} />
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                            <span style={{ fontSize: 13, fontWeight: 500, color: isSelected ? '#ffffff' : 'var(--zinc-300)' }}>
                              {item.label}
                            </span>
                            <span style={{ fontSize: 10, color: 'var(--muted-2)' }}>
                              {item.desc}
                            </span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="syn-editor">
          <div className="syn-empty">
            <div className="syn-empty-orb">
              <Icon name="article" size={32} />
            </div>
            <div className="syn-empty-text">
              <h2>Selecione um documento</h2>
              <p>Cada nó do seu Flow é também um documento. Escolha um na árvore à esquerda ou crie um novo.</p>
            </div>
            <div className="syn-empty-actions">
              <button
                className="syn-empty-act primary"
                onClick={() => {
                  const id = addNode({ label: 'Nova Ideia', type: 'idea', category: 'idea', icon: 'lightbulb', x: 0, y: 0, size: 22 });
                  setSelected(id);
                }}
              >
                <Icon name="add" size={15} /> Novo documento
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Outline / Properties panel */}
      <aside className="syn-outline">
        {selectedNode ? (
          <>
            {headings.length > 0 && (
              <div>
                <div className="syn-out-hd">Outline</div>
                {headings.map((h, i) => (
                  <div key={i} className={`syn-out-item${h.level === 2 ? ' is-h2' : ''}`}>
                    <span style={{ fontFamily: 'var(--font-mono)', fontSize: 9, color: 'var(--muted-2)', width: 14 }}>
                      H{h.level}
                    </span>
                    <span>{h.text}</span>
                  </div>
                ))}
              </div>
            )}

            <div>
              <div className="syn-out-hd">
                Nós linkados{backlinks.length > 0 ? ` — ${backlinks.length}` : ''}
              </div>
              {backlinks.length > 0 ? (
                backlinks.map((n) => (
                  <div
                    key={n.id}
                    className="syn-backlink"
                    style={{ '--c': n.color } as React.CSSProperties}
                    onClick={() => setSelected(n.id)}
                  >
                    <span className="dot" />
                    <span className="name">{n.label}</span>
                  </div>
                ))
              ) : (
                <div style={{ fontSize: 11, color: 'var(--muted)', padding: '4px 2px', lineHeight: 1.5 }}>
                  Sem conexões. Conecte este nó a outros no Flow para vê-los aqui.
                </div>
              )}
            </div>
          </>
        ) : (
          <>
            <div className="syn-out-hd">Ações rápidas</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <div
                className="syn-out-item"
                onClick={() => {
                  const id = addNode({ label: 'Nova Ideia', type: 'idea', category: 'idea', icon: 'lightbulb', x: 0, y: 0, size: 22 });
                  setSelected(id);
                }}
              >
                <Icon name="add" size={13} /> Novo documento
              </div>
            </div>
          </>
        )}
      </aside>
    </div>
  );
}
